import { useSearchParams } from 'react-router-dom'
import AuthorityPanel from '../components/setup/AuthorityPanel'
import { Icon, PageHeader, TabPanel, Tabs } from '../ds'
import { LimitsTab, BrandTab, StoreTab, ChannelsTab, TestTab } from '../components/setup/tabs'
import { sectionFrom } from '../components/setup/limitsGlance'

/**
 * Authority — the third v5 surface (Ledger · Knowledge · Authority).
 *
 * This replaces `screens/Messaging.tsx`, the old Playbook screen. Phase 2b shipped
 * `AuthorityPanel` but had nowhere to put it, so it was bolted inside the old screen's `agent`
 * tab; it now has the first tab to itself.
 *
 * ## The tab set, and what is deliberately not in it
 *
 * `settings-consolidation.md` §3.6 consolidates 12 settings sections into six destinations:
 * Authority · Limits · Voice · Store · Channels · Test. **All six are here.** (Voice is labelled
 * **Brand** since MAR-384 — it now carries how she looks as well as how she sounds; its key and
 * `?tab=voice` links are unchanged.) Store and
 * Channels arrived in phase 2c PR 2, relocated out of `screens/Account.tsx`, which keeps
 * only what is genuinely account-level: billing, team, notifications and profile.
 *
 * ⚠️ Shopify sits on **Store**, not Channels. §3.6 gives Store the Shopify connection, and
 * the v5 prototype's Channels tab renders email and text messages only — Shopify is where
 * the shop's facts are read FROM, not a channel Marva speaks through.
 *
 * The old IA's tabs are gone, each with its control moved to where the fact it governs lives:
 * MAR-296 moved Sales' mention grant onto Limits as the card under the discount ceiling,
 * MAR-398 moved Agent's one switch, `askMerchantEnabled`, onto the `ask_merchant` coverage row
 * of the Authority tab, and MAR-112 deleted Touchpoints along with the per-campaign gate it
 * wrote (`browseFollowUp`, `campaigns.*`). The conversations she starts are governed by her
 * authority now, so Touchpoints' control is the switch on the `originate_followup` coverage row.
 * `?tab=sales`, `?tab=agent` and `?tab=touchpoints` are aliased at the read below.
 *
 * ## `?tab=` is part of the contract, not a convenience
 *
 * The design assumes an exception is made *from a receipt in the Ledger* — a link out of one
 * surface into a specific tab of this one. So those links need somewhere to land, and a merchant
 * needs to be able to send one to themselves. An unknown value falls back to the first tab rather
 * than rendering nothing.
 *
 * ⚠️ `Account.tsx` also has a `?tab=`, and this screen deliberately does NOT implement it the same
 * way — see the comment on the derivation below. Account seeds React state from the parameter once
 * at mount, which is enough for a bookmark and not enough for a link followed from inside the app.
 */

type Tab = 'authority' | 'limits' | 'voice' | 'store' | 'channels' | 'test'

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'authority', label: 'Authority', icon: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z' },
  { key: 'limits', label: 'Limits', icon: 'M12 6v6l4 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z' },
  // The key stays `voice` (MAR-384): every `?tab=voice` link and bookmark keeps landing here.
  { key: 'voice', label: 'Brand', icon: 'M8 10h8M8 14h5M21 12a9 9 0 1 1-3.5-7.1L21 4z' },
  { key: 'store', label: 'Store', icon: 'M5 10.5V19h14v-8.5|M3.5 6.5h17l-.9 3.2a2.3 2.3 0 0 1-4.5.1 2.3 2.3 0 0 1-4.6 0 2.3 2.3 0 0 1-4.6 0L3.5 6.5z|M10 19v-4h4v4' },
  { key: 'channels', label: 'Channels', icon: 'M4 7h16v10H4zM4 8l8 5 8-5|M8 21h8' },
  { key: 'test', label: 'Test', icon: 'M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-9V3' },
]

const TAB_VALUES: Tab[] = TABS.map((t) => t.key)
const DEFAULT_TAB: Tab = 'authority'

/**
 * Retired tab keys that still have links and bookmarks pointing at them, and where each now
 * lives. Resolved at the read like everything else here — never by a redirect in an effect,
 * which would paint the fallback tab for a frame and push a history entry Back walks into.
 * The URL keeps saying `sales` (or `agent`, `touchpoints`); the screen shows what that link was
 * always for.
 */
const TAB_ALIASES: Record<string, Tab> = {
  // MAR-296: the mention grant is the card under the discount ceiling on Limits.
  sales: 'limits',
  // MAR-398: the escalate-to-you switch is on the `ask_merchant` row of the Authority tab.
  agent: 'authority',
  // MAR-384 relabelled Voice as Brand, so `?tab=brand` is the link a merchant would write by hand.
  brand: 'voice',
  // MAR-112: the follow-up switch is on the `originate_followup` row of the Authority tab.
  touchpoints: 'authority',
}

const TAB_ITEMS = TABS.map((t) => ({ value: t.key, label: t.label, icon: <Icon d={t.icon} /> }))
const ID_BASE = 'authority'

export default function Authority() {
  const [searchParams, setSearchParams] = useSearchParams()

  // ⚠️ The URL is the tab's only source of truth — there is no `useState` mirroring it.
  //
  // Holding the tab in state and seeding it from `?tab=` at mount (which is what
  // `screens/Account.tsx` does) reads fine and breaks two things this screen has promised.
  // A second in-app navigation to `/authority?tab=limits` while already here would change the
  // URL and not the screen — and a link out of a Ledger receipt into a named tab is the whole
  // reason this parameter exists. Browser Back would likewise walk the history entries each
  // click pushed without moving the tab.
  //
  // Derived at the read, the URL and the screen cannot disagree, and Back works by construction.
  const raw = searchParams.get('tab')
  const requested = (raw && Object.prototype.hasOwnProperty.call(TAB_ALIASES, raw) ? TAB_ALIASES[raw] : raw) as Tab | null
  const tab: Tab = requested && TAB_VALUES.includes(requested) ? requested : DEFAULT_TAB

  const setActive = (t: Tab) => {
    // ⚠️ Merge, never replace. `setSearchParams({ tab: t })` drops every other parameter, so a
    // receipt link like `?tab=limits&from=ledger&decision=abc` loses the context that says why
    // the merchant is here the moment they touch a second tab.
    const next = new URLSearchParams(searchParams)
    // `?section=` belongs to the tab it was opened on (Limits' and Brand's section navs, MAR-385/384). Carried into
    // another tab it would be meaningless there — or, on a tab with sections of its own, open the
    // wrong one — so a tab switch drops it. Re-selecting the current tab keeps it.
    if (t !== tab) next.delete('section')
    // Re-selecting Limits from the `sales` alias rewrites `tab` to `limits`, which loses what the
    // alias meant (Shopify codes). Pin the section the screen is showing, so the click is a no-op.
    else if (raw === 'sales' && t === 'limits') next.set('section', sectionFrom(searchParams))
    next.set('tab', t)
    setSearchParams(next)
  }

  // The wide canvas (1280px), like Ledger and Knowledge: the design draws Authority at that width,
  // and the sectioned tabs (section nav · section · aside) need it (MAR-395).
  return (
    <div className="mv-canvas-wide">
      <PageHeader
        eyebrow="Authority"
        title={
          <>
            What she may do, and <span className="mv-em">where she stops.</span>
          </>
        }
        sub="The rung she works at, the limits she will not cross, and the voice she uses. Change anything here and she follows it from her next message."
      />

      {/* Underline tabs (D-18): tab roles, arrow keys move between them. */}
      <Tabs tabs={TAB_ITEMS} value={tab} onChange={(v) => setActive(v as Tab)} label="Authority sections" idBase={ID_BASE} />

      {TABS.map((t) => (
        <TabPanel key={t.key} value={t.key} active={tab} idBase={ID_BASE}>
          {/* Limits, Brand and Store are sectioned (nav · section · aside) and use the whole wide canvas.
              The single-column forms keep a reading measure, or a label sits a screen away from its
              control (MAR-395); each loses it when its tab is redesigned. */}
          {t.key === 'authority' && <div className="mv-measure mv-measure--wide"><AuthorityPanel /></div>}
          {t.key === 'limits' && <LimitsTab />}
          {t.key === 'voice' && <BrandTab />}
          {t.key === 'store' && <StoreTab />}
          {t.key === 'channels' && <div className="mv-measure"><ChannelsTab /></div>}
          {t.key === 'test' && <div className="mv-measure"><TestTab /></div>}
        </TabPanel>
      ))}
    </div>
  )
}
